import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Redirect, useHistory } from 'react-router-dom';
import CheckIcon from '@material-ui/icons/Check';
import RemoveIcon from '@material-ui/icons/Remove';
import { FieldArray, Form, Formik, FormikProps } from 'formik';
import Tooltip from '@cpg-its/ads.common.tooltip';
import Button from '@cpg-its/ads.common.button';
import Banner from '@cpg-its/ads.containers.banner';
import { Grid } from '@mui/material';
import { TcsmStates } from '../../../../root/tcsm-model';
import { AnnualEnrollmentProps, MidYearProps } from '../../models/arc-epi-props';
import { MidYearConfirmationFormValue, MidYearEnrollment, MidYearPlanType } from './mid-year-model';
import { AEOfferingPlan, AEPlanType, Enrollment, ProcessingStatus } from '../../../annual-enrollment/models/annual-enrollment-models';
import { useParentRoute } from '../../../../global/hooks/useParentRoute';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { YesNoIndicators } from '../../../../global/models/api-model';
import { getRelationshipLabel, getTierLabel } from '../../../annual-enrollment/helpers/annual-enrollment-helpers';
import { tcsmColors } from '../../../../global/constants/theme/theme-constants/colors';
import { FormField } from '../../../../global/components/form-element/form-field';
import { getRemainingCharacterCounter } from './mid-year-helpers';
import { BillingAccount } from '../billing-accounts/billing-accounts-models';
import { None } from '../../constants/constant-strings';
import { getCurrencyFormat } from '../../../../global/helpers/format-helpers';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { TcsmModal } from '../../../../global/components/tcsm-modal';
import { ButtonWrapper } from '../../../../global/components/layout/button-wrapper';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';
import { AnnualEnrollmentRoutes } from '../../../annual-enrollment/constants/annual-enrollment-routes';
import { deleteFormClick } from './mid-year-requests';
import { midyearSelectionStyles } from '../../../../global/constants/theme/styles/pages/mid-year/midyear-selection-layout';
import { selectedReviewItemStyles } from '../../../../global/constants/theme/styles/components/selected-review-item';
import { gridPageStyles } from '../../../../global/constants/theme/styles/layout/grid-page-layout';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { adjustForLocalOffset } from '../../../../global/helpers/date-helpers';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { labelSupplementStyles } from '../../../../global/constants/theme/styles/components/label-supplement-styles';
import { FormGroup } from '../../../../global/components/layout/form-group';
import { LabelSettingProps } from '../../../../global/models/epi-model';





interface PlanItemProps {
	planType: MidYearPlanType,
	title: string,
}

export function MidYearConfirmation() {
	const pageClasses = gridPageStyles();
	const midYearSettings = useSelector<TcsmStates, MidYearProps>(a => a.epiProps?.midYearSettingsState?.epiContent);
	const annualEnrollmentSettings = useSelector<TcsmStates, AnnualEnrollmentProps>(a => a.epiProps?.annualEnrollmentSettingsState?.epiContent);
	const midYearState = useSelector<TcsmStates, MidYearEnrollment>(a => a.midYearEnrollment?.currentMidYearEnrollment);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(a => a.epiProps?.labelSettingState?.epiContent);
	const parentRoute = useParentRoute();
	const billingAccounts = useSelector<TcsmStates, BillingAccount[]>(state => state.billingAccountsState.billingAccounts);
	const dispatch = useDispatch();
	const groupId = useSelector<TcsmStates, number>(a => a.groupsState?.currentGroup?.group_id);
	const personGuid = useSelector<TcsmStates, string>(a => a.employmentState?.currentEmployee?.guid);
	const history = useHistory();
	const [open, setOpen] = React.useState(false);
	const isHeldForReview = midYearState?.form_record?.processing_status === ProcessingStatus.InProgress && 
							(midYearState?.form_record?.processing_substatus === ProcessingStatus.HeldForReview || midYearState?.form_record?.processing_substatus === ProcessingStatus.Failed);
	const gpsItem = useGpsType(isHeldForReview ? GpsType.MID_YEAR_IN_HELD : GpsType.MID_YEAR_IN_PROGRESS);
	const isHeldMessages = [].concat(midYearState?.processing_messages?.mc_holds)?.concat(midYearState?.processing_messages?.rq_holds)?.concat(midYearState?.processing_messages?.tx_holds)?.filter(a => !!a);
	const handleClose = () => {
		setOpen(false);
	};
	const handleOpen = () => {
		setOpen(true);
	};

	const abandon = midYearState?.form_info?.available_workflow_actions?.Abandon;


	const coveragePath = `${useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${AnnualEnrollmentRoutes.Coverage}`)}`;
	const redirectAction = () => {
		history.push(coveragePath);
	};

	const closeModalHandler = () => { setOpen(false); };

	useEffect(() => {
		dispatch(apiErrorClear());
	});

	if (midYearState.form_record?.processing_status !== ProcessingStatus.Completed && 
		midYearState.form_record?.processing_status !== ProcessingStatus.InProgress) {
		return <Redirect to={parentRoute} />;
	}

	const getInitialCoverageValues = (typeKey: AEPlanType) => {
		const coverage: string[] = [];

		if (midYearState?.enrollments_edited) {

			const eligibleDependents = midYearState?.eligible_dependents;
			for (let i = 0; i < eligibleDependents?.length ?? 0; i += 1) {
				const dependentGuid = eligibleDependents[i]?.person?._guid;
				if (midYearState?.dep_enrollments_edited[dependentGuid][typeKey] === YesNoIndicators.Yes) {
					coverage.push(dependentGuid);
				}
			}
		}

		return coverage;
	};


	const initialValues: MidYearConfirmationFormValue = {
		date_of_qualifying_event: midYearState?.global_edited?.date_of_qualifying_event ? adjustForLocalOffset(new Date(midYearState?.global_edited?.date_of_qualifying_event)).toString() : null,

		[AEPlanType.Medical]: getInitialCoverageValues(AEPlanType.Medical),
		[AEPlanType.Dental]: getInitialCoverageValues(AEPlanType.Dental),

		memo: midYearState?.global_edited?.memo,
		specialRequests: midYearState?.global_edited?.special_requests,
		decline_reason: midYearState?.global_edited?.decline_reason,
		decline_reason_other: midYearState?.global_edited?.decline_reason_other,
		extenuating_circumstances: midYearState?.global_edited?.extenuating_circumstances,
	};


	const getSecondaryLabel = (checked: boolean, groupName: AEPlanType, value: string) => {
		if (checked && (midYearState.dep_enrollments_base[value] && midYearState.dep_enrollments_base[value][groupName] === YesNoIndicators.No)) {
			return [annualEnrollmentSettings?.addedText, 'green-text'];
		}

		if (!checked && (midYearState.dep_enrollments_base[value] && midYearState.dep_enrollments_base[value][groupName] === YesNoIndicators.Yes)) {
			return [annualEnrollmentSettings?.removedText, 'red-text'];
		}

		return null;
	};

	function RenderDependentsEmptyMessage(enrollments: Enrollment[], notEnrolledTerminatedMessage: string){
		if (enrollments?.length > 0 || !notEnrolledTerminatedMessage) return null;

		return (<>
			<WysiwygWithReactLink content={notEnrolledTerminatedMessage} />			
		</>);
	}


	const renderCoverage = (formProps: FormikProps<MidYearConfirmationFormValue>, groupName: AEPlanType, groupLabel: string, notEnrolledTerminatedMessage: string) => {
		const selectedItemClasses = selectedReviewItemStyles();
		return (
			<>
				<FieldArray
					name={groupName}
					render={() => (
						<div className={`divider-section-item ${selectedItemClasses.selectedReviewItem}`}>

							<h4 className='selected-review-item-header'>{groupLabel}</h4>							

							{ RenderDependentsEmptyMessage(formProps?.values[groupName], notEnrolledTerminatedMessage) }

							{midYearState?.eligible_dependents?.map(dep => {
								const dependentChecked = formProps?.values[groupName]?.includes(dep?.person?._guid);
								const dependentSecondaryLabelOptions = getSecondaryLabel(dependentChecked, groupName, dep?.person?._guid);

								return (!dependentChecked && !dependentSecondaryLabelOptions ? null :

									<div className='selected-review-item-inner' >
										<div className='selected-review-item-content'>
											{dependentChecked && <CheckIcon className='selected-review-item-icon added' />}
											{(!dependentChecked && dependentSecondaryLabelOptions) && <RemoveIcon className='selected-review-item-icon removed' />}
											<div>
												<p className='input-label'>{`${dep?.person?.first} ${dep?.person?.middle} ${dep?.person?.last}  ${dep?.person?.suffix}`}</p>
												<p className='input-caption'>{getRelationshipLabel(dep?.relationship_code, annualEnrollmentSettings)}</p>

											</div>
										</div>
										<p className={`input-label selected-review-item-subtext ${dependentSecondaryLabelOptions && dependentSecondaryLabelOptions[1]}`}>{dependentSecondaryLabelOptions && dependentSecondaryLabelOptions[0]}</p>
									</div>

								);
							})}
						</div>
					)}
				/>
			</>
		);
	};

	const PlanItem = (planProps: PlanItemProps) => {
		const showVolume = planProps?.planType !== MidYearPlanType.Dental && planProps?.planType !== MidYearPlanType.Medical;
		const original = midYearState?.enrollments_base[planProps.planType] && midYearState?.enrollments_base[planProps.planType] ? midYearState?.enrollments_base[planProps.planType][0] as Enrollment : null;
		const edited = midYearState?.enrollments_edited[planProps.planType] && midYearState?.enrollments_edited[planProps.planType] ? midYearState?.enrollments_edited[planProps.planType][0] as Enrollment : null;
		const planChanged = edited && original?.code !== edited?.code;
		const billingAccountChanged = edited && original?.account_nbr !== edited?.account_nbr;
		const tierChanged = edited && original?.tier !== edited?.tier;
		const rateChanged = edited && original?.rate !== edited?.rate;
		const effectiveDateChanged = edited && original?.effective_date !== edited?.effective_date;
		const terminationDateChanged = edited && original?.termination_date !== edited?.termination_date;
		const volumeChanged = edited && original?.volume !== edited?.volume;
		const editedBillingAccount = billingAccounts?.filter(a => a.account_number === edited?.account_nbr)[0];
		const originBillingAccount = billingAccounts?.filter(a => a.account_number === original?.account_nbr)[0];
		const editedPlan = midYearState.offerings[planProps.planType]?.plans.filter(a => a.code === edited?.code)[0] as AEOfferingPlan;
		const originPlan = midYearState.offerings[planProps.planType]?.plans.filter(a => a.code === edited?.code)[0] as AEOfferingPlan;
		const planClasses = midyearSelectionStyles();
		return (
			<div className={`${planClasses.midYearSelectionContainer} midyear-selection-section`}>
				<h4>{planProps.title}</h4>
				<div className='midyear-selection'>
					<div className='midyear-selection-plan-item'>
						<div className='midyear-selection-plan-item-header'>
							<p className='input-label midyear-selection-plan-item-label'>{midYearSettings.billingAccountLabel}</p>
							<p className='input-label midyear-selection-plan-item-text-new'><em>{billingAccountChanged && midYearSettings.newText}</em></p>
						</div>
						{editedBillingAccount ? <p className='input-label midyear-selection-plan-item-text-new'>{editedBillingAccount?.account_number} {editedBillingAccount?.account_name}</p> : <p >{originBillingAccount?.account_number || None} {originBillingAccount?.account_name}</p>}
						{(billingAccountChanged && original?.account_nbr !== None) && <><p className='input-label midyear-selection-plan-item-text-original'>{midYearSettings.originalText} {midYearSettings.billingAccountLabel}:{originBillingAccount?.account_number || None} {originBillingAccount?.account_name}</p></>}
					</div>
					<div className='midyear-selection-plan-item'>
						<div className='midyear-selection-plan-item-header'>
							<p className='input-label midyear-selection-plan-item-label'>{midYearSettings.planLabel}</p>
							<p className='input-label midyear-selection-plan-item-text-new'><em>{planChanged && midYearSettings.newText}</em></p>
						</div>
						{planChanged ? <p className='input-label midyear-selection-plan-item-text-new'>{editedPlan.name}</p> : <p>{originPlan?.name || edited?.name || edited?.code || None}</p>}
						{(planChanged && original?.code !== None) && <><p className='midyear-selection-plan-item-text-original caption'><strong>{midYearSettings.originalText} {midYearSettings.planLabel}</strong>: {originPlan?.name || None}</p></>}
					</div>
					<div className='midyear-selection-plan-item'>
						<div className='midyear-selection-plan-item-header'>
							<p className='input-label midyear-selection-plan-item-label'>{showVolume ? midYearSettings.volumeLabel : midYearSettings.tierLabel}</p>
							{volumeChanged || tierChanged && <p className='input-label midyear-selection-plan-item-text-new'><em>{midYearSettings.newText}</em></p>}
						</div>
						{
							volumeChanged || tierChanged ?
								<p className='input-label midyear-selection-plan-item-text-new'>
									{
										showVolume ?
											edited?.volume && getCurrencyFormat(parseFloat(edited.volume)) :
											getTierLabel(edited.tier, annualEnrollmentSettings)
									}
								</p> :
								<p>
									{
										showVolume ?
											getCurrencyFormat(parseFloat(original.volume)) :
											getTierLabel(original.tier, annualEnrollmentSettings)
									}
								</p>
						}
						{
							((volumeChanged || tierChanged) && (original?.volume || original?.tier)) &&
							<>
								<p className='midyear-selection-plan-item-text-original caption'>
									<strong>{midYearSettings.originalText} {showVolume ? midYearSettings?.volumeLabel : midYearSettings?.tierLabel}: </strong>{showVolume ?
										getCurrencyFormat(parseFloat(original.volume)) :
										getTierLabel(original.tier, annualEnrollmentSettings)}
								</p>
							</>
						}
					</div>
					<div className='midyear-selection-plan-item'>
						<div className='midyear-selection-plan-item-header'>
							<p className='input-label midyear-selection-plan-item-label'>{midYearSettings.rateLabel}</p>
							{rateChanged && <p className='input-label midyear-selection-plan-item-text-new'><em>{midYearSettings.newText}</em></p>}
						</div>
						{rateChanged ? <p className='input-label midyear-selection-plan-item-text-new'>{getCurrencyFormat(parseFloat(edited?.rate))}</p> : <p>{getCurrencyFormat(parseFloat(original?.rate))}</p>}
						{(rateChanged && original?.rate) && <p className='midyear-selection-plan-item-text-original caption'><strong>{midYearSettings.originalText} {midYearSettings.rateLabel}</strong>: {getCurrencyFormat(parseFloat(original.rate), labelSettings)}</p>}
					</div>
					<div className='midyear-selection-plan-item'>
						<div className='midyear-selection-plan-item-header'>
							<p className='input-label midyear-selection-plan-item-label'>{midYearSettings.effectiveDateLabel}</p>
							{effectiveDateChanged && <p className='input-label midyear-selection-plan-item-text-new'><em>{midYearSettings.newText}</em></p>}
						</div>


						{effectiveDateChanged ? <p className='input-label midyear-selection-plan-item-text-new'>{edited.effective_date}</p> : <p>{original?.effective_date}</p>}
						{(effectiveDateChanged && original?.effective_date) && <><p className='midyear-selection-plan-item-text-original caption'><strong>{midYearSettings.originalText} {midYearSettings.effectiveDateLabel}</strong>: {original?.effective_date}</p></>}
					</div>
					<div className='midyear-selection-plan-item'>
						<div className='midyear-selection-plan-item-header'>
							<p className='input-label midyear-selection-plan-item-label'>{midYearSettings.terminateDateLabel}</p>
							{terminationDateChanged && <p className='input-label midyear-selection-plan-item-text-new'><em>{midYearSettings.newText}</em></p>}
						</div>
						{terminationDateChanged ? <p className='input-label midyear-selection-plan-item-text-new'>{edited.termination_date}</p> : <p>{original?.termination_date}</p>}
						{(terminationDateChanged && original?.termination_date) && <><p className='midyear-selection-plan-item-text-original caption'><strong>{midYearSettings.originalText} {midYearSettings.terminateDateLabel}</strong>: {original?.termination_date}</p></>}
					</div>
				</div>
			</div>
		);
	};

	const renderComments = (formProps: FormikProps<MidYearConfirmationFormValue>) => {
		const labelClasses = labelSupplementStyles();
		return (
			<DividerSection removeDivider>
				<FormGroup>
					<h5>{annualEnrollmentSettings.myNotesLabel}</h5>
					<FormField
						name='memo'
						label=''
						component='input'
						multiline
						rows={5}
						touched={formProps.touched}
						errors={formProps.errors}
						disabled
					/>
					<Grid container justifyContent='flex-end'>
						<p className='input-caption'>{`${getRemainingCharacterCounter(formProps?.values?.memo?.length)}/255 ${midYearSettings.characterRemainingText || ''}`}</p>
					</Grid>
					<div className={labelClasses.labelLayout}>
						<h5>{annualEnrollmentSettings.specialRequestsLabel}</h5>
						<div className='supplement-wrapper'>
							<Tooltip iconColor={tcsmColors.blue} title={midYearSettings.specialRequestTooltip} />
						</div>
					</div>

					<FormField
						name='specialRequests'
						label={annualEnrollmentSettings.specialRequestsDescription}
						component='input'
						multiline
						rows={5}
						touched={formProps.touched}
						errors={formProps.errors}
						disabled
					/>
					<Grid container justifyContent='flex-end'>
						<p className='input-caption'>{`${getRemainingCharacterCounter(formProps?.values?.specialRequests?.length)}/255 ${midYearSettings.characterRemainingText || ''}`}</p>
					</Grid>
					
					{
						formProps?.values?.extenuating_circumstances?.trim().length > 0 &&
						<>
							<h5>{midYearSettings.extenuatingCircumstancesLabel}</h5>
							<FormField
								name='extenuating_circumstances'
								label=''
								component='input'
								multiline
								rows={5}
								touched={formProps.touched}
								errors={formProps.errors}
								disabled
							/>
							<Grid container justifyContent='flex-end'>
								<p className='input-caption'>{`${getRemainingCharacterCounter(formProps?.values?.extenuating_circumstances?.length)}/255 ${midYearSettings.characterRemainingText || ''}`}</p>
							</Grid>
						</>
					}
				</FormGroup >
			</DividerSection>
		);
	};


	return (
		<div className={pageClasses.gridPageContainer}>
			<div className='grid-main-content'>
				<ContentSpacer>
					<Banner
						banner={{
							type: isHeldForReview ? 'warning' : 'info',
							label: <h5>{isHeldForReview ? midYearSettings.midYearConfirmationHeldTitle : midYearSettings.midYearConfirmationTitle}</h5>,
							description:
								<div className='rich-text'>
									{
										isHeldForReview && <WysiwygWithReactLink content={midYearSettings.midYearConfirmationHeldDescription} />
									}
									{
										isHeldForReview && isHeldMessages?.map((a, idx) => <p key={idx}>{a}</p>)
									}
								</div>,
						}}
					/>
				</ContentSpacer>
				<ContentSpacer>
					<WysiwygWithReactLink content={midYearSettings.midYearConfirmationDescription} />
				</ContentSpacer>

				<PageHeader headerText={midYearSettings.coveragePageTitle} />
			</div>
			<div className='grid-sidebar'>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
				</SideBarContent>
			</div>
			<div className='grid-main-content'>
				<DividerSection>
					<p className='input-label'>{midYearSettings.dateOfQualifyingLabel}</p>
					<p>{midYearState?.global_edited?.date_of_qualifying_event}</p>
				</DividerSection>
			</div>
			<Formik
				initialValues={initialValues}
				enableReinitialize
				onSubmit={null}
			>
				{(formProps: FormikProps<MidYearConfirmationFormValue>) => (
					<>
						<Form className='grid-fw-content grid-nested'>

							<FormWrapper
								formikProps={formProps}
								submitText=''
								cancelText=''
								onCancel={null}
								hideButtons
							>

								<div className='grid-main-content'>
									<DividerSection removeDivider sectionText={midYearSettings?.participantSelectionLabel} >
										<ContentSpacer>
											{renderCoverage(formProps, AEPlanType.Medical, annualEnrollmentSettings?.medicalCoverageLabel, midYearSettings?.medicalParticipantNotEnrolledTerminatedMessage)}
										</ContentSpacer>
											{renderCoverage(formProps, AEPlanType.Dental, annualEnrollmentSettings?.dentalCoverageLabel, midYearSettings?.dentalParticipantNotEnrolledTerminatedMessage)}

									</DividerSection>
								</div>
								<div className='grid-fw-content'>
									<DividerSection topDivider sectionText={annualEnrollmentSettings?.currentPlanLabel}>
										<PlanItem planType={MidYearPlanType.Medical} title={midYearSettings.medicalPlanTitle} />
									</DividerSection>
									<DividerSection dashedBorder>
										<PlanItem planType={MidYearPlanType.Dental} title={midYearSettings.dentalPlanTitle} />
									</DividerSection>
									<DividerSection dashedBorder>
										<PlanItem planType={MidYearPlanType.Group} title={midYearSettings.groupLifePlanTitle} />
										<PlanItem planType={MidYearPlanType.SuppLife} title={midYearSettings.supplementalLifePlanTitle} />
										<PlanItem planType={MidYearPlanType.SuppSpouse} title={midYearSettings.supplementalSpouseTitle} />
									</DividerSection>
									<DividerSection>
										<PlanItem planType={MidYearPlanType.LongTermDisable} title={midYearSettings.longTermTitle} />
										<PlanItem planType={MidYearPlanType.ShortTermDisable} title={midYearSettings.shortTermTitle} />
										{midYearState?.offerings?.U && <PlanItem planType={MidYearPlanType.Unemployment} title={midYearSettings.unemploymentInsuranceTitle} />}
									</DividerSection>
								</div>
								{renderComments(formProps)}
							</FormWrapper>

						</Form>
					</>
				)}
			</Formik>
			{
				(!isHeldForReview && abandon) &&
				<>
					<div className='grid-main-content'>
						<DividerSection sectionText={midYearSettings.deleteFormTitle} removeDivider>
							<ContentSpacer>
								<div dangerouslySetInnerHTML={{ __html: midYearSettings.deleteFormDescription }}></div>
							</ContentSpacer>
							<Button buttonStyle='secondary' type='button' onClick={handleOpen} text={midYearSettings.deleteFormButtonText || 'Delete Form'} />
						</DividerSection>
					</div>
					<TcsmModal
						open={open}
						handleClose={handleClose}>
						<>
							<ContentSpacer>
								<div className='rich-text'>
									<div dangerouslySetInnerHTML={{ __html: midYearSettings.deleteFormModalDescription }} />
								</div>
							</ContentSpacer>
							<ButtonWrapper flexEnd>
								<Button type='button' buttonStyle='tertiary' text={midYearSettings.deleteFormModalCancelText || 'Cancel'} backgroundColor='#808080' onClick={handleClose} />
								<Button type='button' buttonStyle='primary' text={midYearSettings.deleteFormModalConfirmText || 'Confirm'} backgroundColor='#0000FF' onClick={() => { deleteFormClick(dispatch, personGuid, groupId, midYearState, redirectAction, closeModalHandler); }} />
							</ButtonWrapper>
						</>

					</TcsmModal>
				</>
			}
		</div>
	);
}

